import { connect } from 'react-redux';
import React from 'react';
/** import third-party libraries in the section above, Sort Lines Ascending */

import { makeStyles, withStyles } from '@mui/styles';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CheckIcon from '@mui/icons-material/Check';
import CircularProgress from '@mui/material/CircularProgress';
import CloseIcon from '@mui/icons-material/Close';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import Radio from '@mui/material/Radio';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
/** import mui components in the section above, Sort Lines Ascending */

import { BlockLoadGroup } from 'widgets-v5/load-group';
import { MuiFormLabel, TextField } from 'widgets-v6/standard-input';
import ClickToCopyButton from 'widgets/click-to-copy-button';
/** import widgets components in the section above, Sort Lines Ascending */

/** import multi application imports in the section above, Sort Lines Ascending */

import { isInternalUser } from 'states/profile/business-rules';
import {
    getSelfPrivilegeChangeState,
    SELF_PRIVILEGE_CHANGE_BLOCKED_MESSAGE,
} from './business-rules';
import actions from './actions';
import selector from './selector';
/** import single application imports in the section above, Sort Lines Ascending */

const useStyles = makeStyles(theme => ({
    dialogTitleRoot: {
        padding: `10px ${theme.spacing(1)} 10px ${theme.spacing(3)}`,
        borderBottom: `1px solid ${theme.palette.grey[400]}`,
    },
    table: {
        marginBottom: theme.spacing(1),
    },
    red: {
        color: theme.palette.error.main,
    },
    roleSectionHeader: {
        marginTop: theme.spacing(2),
        marginBottom: theme.spacing(1),
    },
    roleError: {
        marginTop: theme.spacing(1),
        marginBottom: theme.spacing(1),
    },
    infoAlert: {
        alignItems: 'center',
    },
    warningAlert: {
        alignItems: 'center',
    },
}));

const StyledTableRow = withStyles(theme => ({
    root: {
        '&:nth-of-type(odd)': {
            backgroundColor: theme.palette.background.default,
        },
    },
}))(TableRow);

const Loading = withStyles(() => ({
    root: {
        width: '24px',
        height: '24px',
    },
}))(Grid);

const LoadingSpinner = withStyles(() => ({
    root: {
        width: '24px !important',
        height: '24px !important',
    },
}))(CircularProgress);

const permissionTableRows = [
    {
        label: 'See campaign* reports',
        viewableByOrgTypes: ['client', 'co_managed_client', 'media_buyer', 'admin'],
        authorizedUserTypes: [
            'analyst',
            'media_planner',
            'campaign_manager',
            'account_admin',
            'admin',
            'read_only_super_admin',
        ],
    },
    {
        label: 'See campaign* settings',
        viewableByOrgTypes: ['client', 'co_managed_client', 'media_buyer', 'admin'],
        authorizedUserTypes: [
            'media_planner',
            'campaign_manager',
            'account_admin',
            'admin',
            'read_only_super_admin',
        ],
    },
    {
        label: 'See and create cross-campaign reports',
        viewableByOrgTypes: ['client', 'co_managed_client', 'media_buyer', 'admin'],
        authorizedUserTypes: [
            'media_planner',
            'campaign_manager',
            'account_admin',
            'admin',
            'read_only_super_admin',
        ],
    },
    {
        label: 'See and create inventory forecasts',
        viewableByOrgTypes: ['client', 'co_managed_client', 'media_buyer', 'admin'],
        authorizedUserTypes: [
            'media_planner',
            'campaign_manager',
            'account_admin',
            'admin',
            'read_only_super_admin',
        ],
    },
    {
        label: 'Create and manage campaign resources (audiences, pixels, app & site lists, etc)',
        viewableByOrgTypes: ['client', 'co_managed_client', 'media_buyer', 'admin'],
        authorizedUserTypes: [
            'media_planner',
            'campaign_manager',
            'account_admin',
            'admin',
            'read_only_super_admin',
        ],
    },
    {
        label: 'Create and manage campaigns',
        viewableByOrgTypes: ['client', 'co_managed_client', 'media_buyer', 'admin'],
        authorizedUserTypes: ['campaign_manager', 'account_admin', 'admin'],
    },
    {
        label: 'Create and manage users',
        viewableByOrgTypes: ['client', 'co_managed_client', 'media_buyer', 'admin'],
        authorizedUserTypes: ['account_admin', 'user_admin', 'admin'],
    },
    {
        label: 'Create and manage self-serve-users',
        viewableByOrgTypes: ['admin'],
        authorizedUserTypes: ['admin'],
    },
];

const userTypes = [
    {
        label: 'Analyst',
        value: 'analyst',
        createableByOrgTypes: ['client', 'co_managed_client', 'media_buyer', 'admin'],
    },
    {
        label: 'Media Planner',
        value: 'media_planner',
        createableByOrgTypes: ['co_managed_client', 'media_buyer', 'admin'],
    },
    {
        label: 'Campaign Manager',
        value: 'campaign_manager',
        createableByOrgTypes: ['media_buyer', 'admin'],
    },
    {
        label: 'Account Admin',
        value: 'account_admin',
        createableByOrgTypes: ['co_managed_client', 'media_buyer', 'admin'],
    },
    {
        label: 'User Admin',
        value: 'user_admin',
        createableByOrgTypes: ['co_managed_client', 'media_buyer', 'admin'],
    },
    {
        label: 'Read-only Super Admin',
        value: 'read_only_super_admin',
        createableByOrgTypes: ['admin'],
    },
    {
        label: 'Super Admin',
        value: 'admin',
        createableByOrgTypes: ['admin'],
    },
];
const AdminUserForm = ({
    dispatch,
    isFormOpen,
    draft,
    isLoading,
    errors,
    showErrors,
    refreshUsers,
    isSubmitting,
    organizationType,
    mode,
    currentUserId,
    originalGlobalRole,
    originalSuspended,
}) => {
    const classes = useStyles();

    const handleClose = () => dispatch(actions.closeForm());
    const handleSubmit = async () => {
        await dispatch(actions.submitDraft(refreshUsers));
    };

    const filteredUserTypes =
        organizationType === 'admin'
            ? userTypes
            : _.filter(
                  userTypes,
                  userType =>
                      userType.value !== 'admin' && userType.value !== 'read_only_super_admin'
              );
    const filteredPermissionTableRows = _.filter(permissionTableRows, row =>
        _.includes(row.viewableByOrgTypes, organizationType)
    );
    const { isSelfUser, isBlockedSelfPrivilegeSave } = getSelfPrivilegeChangeState({
        mode,
        draft,
        targetUserId: draft.id,
        currentUserId,
        originalGlobalRole,
        originalSuspended,
    });

    return (
        <Dialog
            fullWidth
            maxWidth={false}
            open={isFormOpen}
            onClose={handleClose}
            PaperProps={{
                sx: { maxWidth: 1300 },
            }}
        >
            <DialogTitle classes={{ root: classes.dialogTitleRoot }}>
                <Grid container direction="row" justifyContent="space-between" alignItems="center">
                    <div>
                        <Typography variant="h6">
                            {mode === 'create' ? 'New User' : 'Edit User'}
                        </Typography>
                    </div>
                    <div>
                        <IconButton onClick={handleClose} size="large">
                            <CloseIcon />
                        </IconButton>
                    </div>
                </Grid>
            </DialogTitle>
            <DialogContent>
                <BlockLoadGroup isLoading={isLoading}>
                    <Grid container spacing={3}>
                        <Grid item xs={3}>
                            <div>
                                {isInternalUser() && mode === 'edit' && draft.id && (
                                    <FormControl margin="normal" fullWidth>
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 0.5,
                                            }}
                                        >
                                            <MuiFormLabel
                                                component="span"
                                                sx={{
                                                    mb: 0,
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    lineHeight: 1.4375,
                                                }}
                                            >
                                                ID:
                                            </MuiFormLabel>
                                            <Box
                                                component="span"
                                                sx={{
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    lineHeight: 1.4375,
                                                    fontSize: '1rem',
                                                }}
                                            >
                                                {draft.id}
                                            </Box>
                                            <ClickToCopyButton
                                                variant="icon"
                                                textToCopy={String(draft.id)}
                                                textToDisplay="Copy user ID"
                                            />
                                        </Box>
                                    </FormControl>
                                )}
                                <TextField
                                    label="First Name"
                                    onChange={e =>
                                        dispatch(actions.updateDraft('first_name', e.target.value))
                                    }
                                    value={draft.first_name}
                                    error={Boolean(showErrors && errors.first_name)}
                                    errorText={showErrors ? errors.first_name : undefined}
                                    fullWidth
                                    required
                                />
                                <TextField
                                    label="Last Name"
                                    onChange={e =>
                                        dispatch(actions.updateDraft('last_name', e.target.value))
                                    }
                                    value={draft.last_name}
                                    error={Boolean(showErrors && errors.last_name)}
                                    errorText={showErrors ? errors.last_name : undefined}
                                    fullWidth
                                    required
                                />
                                <TextField
                                    label="Email"
                                    onChange={e =>
                                        dispatch(actions.updateDraft('email', e.target.value))
                                    }
                                    value={draft.email}
                                    error={Boolean(showErrors && errors.email)}
                                    errorText={showErrors ? errors.email : undefined}
                                    fullWidth
                                    required
                                />
                            </div>
                        </Grid>
                        <Grid item xs={9}>
                            <div className={classes.roleSectionHeader}>
                                <MuiFormLabel
                                    component="legend"
                                    required
                                    error={Boolean(showErrors && errors.global_role)}
                                    classes={{ asterisk: classes.red }}
                                >
                                    Permissions and Role
                                </MuiFormLabel>
                                {isSelfUser && (
                                    <Box mt={1}>
                                        <Alert severity="warning" className={classes.warningAlert}>
                                            <Typography variant="body1" component="span">
                                                {SELF_PRIVILEGE_CHANGE_BLOCKED_MESSAGE}
                                            </Typography>
                                        </Alert>
                                    </Box>
                                )}
                                {/* Only show the info message for Edit User */}
                                {mode === 'edit' && !isSelfUser && (
                                    <Box mt={1}>
                                        <Alert severity="info" className={classes.infoAlert}>
                                            <Typography variant="body1" component="span">
                                                Note: Changing a user's role will sign them out of
                                                all active sessions. They will need to log in again
                                                to apply the new permissions.
                                            </Typography>
                                        </Alert>
                                    </Box>
                                )}
                            </div>
                            <div>
                                <Table className={classes.table}>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>
                                                {showErrors && errors.global_role && (
                                                    <Typography
                                                        color="error"
                                                        className={classes.roleError}
                                                    >
                                                        {errors.global_role}
                                                    </Typography>
                                                )}
                                            </TableCell>
                                            {_.map(filteredUserTypes, type => (
                                                <TableCell align="center" key={type.value}>
                                                    <FormControlLabel
                                                        value={type.value}
                                                        control={
                                                            <Radio
                                                                color="primary"
                                                                disabled={
                                                                    isSelfUser ||
                                                                    !_.includes(
                                                                        type.createableByOrgTypes,
                                                                        organizationType
                                                                    )
                                                                }
                                                                onChange={e =>
                                                                    dispatch(
                                                                        actions.updateDraft(
                                                                            'global_role',
                                                                            e.target.value
                                                                        )
                                                                    )
                                                                }
                                                            />
                                                        }
                                                        label={type.label}
                                                        labelPlacement="top"
                                                        checked={draft.global_role === type.value}
                                                    />
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {_.map(filteredPermissionTableRows, row => (
                                            <StyledTableRow key={row.label}>
                                                <TableCell>{row.label}</TableCell>
                                                {_.map(filteredUserTypes, user => (
                                                    <TableCell key={user.value} align="center">
                                                        {_.includes(
                                                            row.authorizedUserTypes,
                                                            user.value
                                                        ) && <CheckIcon color="primary" />}
                                                    </TableCell>
                                                ))}
                                            </StyledTableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                                <Typography>*campaigns shared with their organization</Typography>
                            </div>
                        </Grid>
                    </Grid>
                </BlockLoadGroup>
            </DialogContent>
            <DialogActions>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSubmit}
                    disabled={isBlockedSelfPrivilegeSave}
                >
                    {isSubmitting ? (
                        <Loading>
                            <LoadingSpinner fontSize="small" color="inherit" />
                        </Loading>
                    ) : (
                        'Save'
                    )}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default connect(selector)(AdminUserForm);
